import { action, makeObservable, observable, runInAction } from 'mobx';
import axios from 'axios';

// 회사 정보 캐시.
// admin AppStore 의 company 패턴 (symbolLogo.fullUrl / logoFile.fullUrl) 을 그대로 차용.
// front 는 customer entry 라 로그인 세션 없이 진입하므로 별도 store + localStorage 캐시.
//
// 응답 스키마 (POST /api/v1/company/list).items[*] 중 본 store 가 사용하는 필드만:
//   { id, companyName, logoFile?: { fullUrl }, symbolLogo?: { fullUrl } }
// documentFile / registrationNumber 등 대용량·민감 필드는 캐시에 저장하지 않음.

const CACHE_KEY_PREFIX = 'company_cache_';
// 캐시 신선도 — 1 시간. 로고 변경 발생 빈도 낮음 + 정적 m.daishin.com URL.
const CACHE_TTL_MS = 60 * 60 * 1000;

// link[rel=icon] 을 새 URL 로 교체. admin AppStore.updateFavicon 패턴 동일.
const updateFavicon = (url) => {
  const existing = document.querySelector("link[rel='icon']");
  if (existing) existing.parentNode.removeChild(existing);
  const link = document.createElement('link');
  link.rel = 'icon';
  link.href = url;
  document.head.appendChild(link);
};

// 캐시·메모리에 보관할 필드만 추출. 큰 base64 (documentFile) 와 운영 메타 제외.
const pickCompanyFields = (raw) => {
  if (!raw) return null;
  const { id, companyName, logoFile, symbolLogo } = raw;
  return {
    id,
    companyName,
    logoFile: logoFile?.fullUrl ? { fullUrl: logoFile.fullUrl } : null,
    symbolLogo: symbolLogo?.fullUrl ? { fullUrl: symbolLogo.fullUrl } : null,
  };
};

const readCache = (id) => {
  try {
    const raw = localStorage.getItem(`${CACHE_KEY_PREFIX}${id}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.cachedAt || Date.now() - parsed.cachedAt > CACHE_TTL_MS) {
      return null;
    }
    return parsed.data ?? null;
  } catch {
    return null;
  }
};

const writeCache = (id, data) => {
  try {
    localStorage.setItem(
      `${CACHE_KEY_PREFIX}${id}`,
      JSON.stringify({ cachedAt: Date.now(), data }),
    );
  } catch {
    // [임시] storage quota / private mode 무시
  }
};

class CompanyStore {
  @observable
  company = null;

  @observable
  isLoading = false;

  inflight = new Map();

  constructor() {
    makeObservable(this);
  }

  @action.bound
  setCompany(company) {
    this.company = company;
    // index.js / Loading.js:156 의 `${brandName} 디지털 상담` 패턴과 동일하게 동기화.
    // /landing 직접 진입 (ARS 응답 없음) 케이스에서도 회사명 반영.
    if (company?.companyName) {
      document.title = `${company.companyName} 디지털 상담`;
    }
    // admin AppStore.updateFavicon 패턴 차용.
    const faviconUrl = company?.symbolLogo?.fullUrl ?? company?.logoFile?.fullUrl;
    if (faviconUrl) {
      updateFavicon(faviconUrl);
    }
  }

  // 캐시 우선 + 미스 시 fetch. 동시 호출 dedupe.
  @action.bound
  async fetchCompany(id) {
    if (!id) return null;

    const cached = readCache(id);
    if (cached) {
      this.setCompany(cached);
      return cached;
    }

    if (this.inflight.has(id)) {
      return this.inflight.get(id);
    }

    const promise = (async () => {
      runInAction(() => {
        this.isLoading = true;
      });
      try {
        // baseURL 토글 영향 회피 — 절대경로 사용
        const res = await axios.post(
          '/api/v1/company/list',
          { status: '', keyword: '' },
          { baseURL: '' },
        );
        const items = res?.data?.items ?? [];
        const raw = items.find((c) => String(c.id) === String(id)) ?? null;
        const picked = pickCompanyFields(raw);
        if (picked) {
          writeCache(id, picked);
          this.setCompany(picked);
        }
        return picked;
      } catch (error) {
        console.error('CompanyStore.fetchCompany 실패:', error);
        return null;
      } finally {
        runInAction(() => {
          this.isLoading = false;
        });
        this.inflight.delete(id);
      }
    })();

    this.inflight.set(id, promise);
    return promise;
  }
}

const companyStore = new CompanyStore();
window.companyStore = companyStore;
export default companyStore;
